<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcCompanyBankAccountCard',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  const act_7315092382398562_reloadComponentData_uvilda2f4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_yon383lfc =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_uvilda2f4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_yon383lfc],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_yon383lfc,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_yon383lfc = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_yon383lfc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox direction="column">
      <ItpBox spacing="mb-2">
        <ItpText :text="item.account_number"> </ItpText>
        <ItpBadge
          :text="gettext('Default')"
          class="mx-4"
          v-if="item.is_default"
        >
        </ItpBadge>
      </ItpBox>
      <UcValidityPeriod
        :from="item.valid_from"
        :to="item.valid_to"
        class="mb-2"
        v-if="item.valid_from || item.valid_to"
      >
      </UcValidityPeriod>
      <ItpText
        :text="item.description"
        pre
        spacing="mb-2"
        v-if="item.description"
      >
      </ItpText>
      <ItpText
        :title="pgettext('BankAccount', 'Bank')"
        :text="item.bank"
        spacing="mb-2"
        v-if="item.bank"
      >
      </ItpText>
      <ItpText
        :title="pgettext('BankAccount', 'IBAN')"
        :text="item.iban"
        spacing="mb-2"
        v-if="item.iban"
      >
      </ItpText>
    </ItpBox>
  </ItpUsercontrol>
</template>
